import {
  addMonths,
  eachDayOfInterval,
  endOfMonth,
  endOfWeek,
  format,
  isSameDay,
  isSameMonth,
  isWithinInterval,
  parseISO,
  startOfMonth,
  startOfWeek,
  subMonths,
} from 'date-fns';
import { toDateOnlyString } from './dateDisplay';

export type CalendarView = 'days' | 'months' | 'years';

export const WEEKDAY_LABELS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'] as const;

export const MONTH_LABELS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
] as const;

export const MONTH_SHORT = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
] as const;

export function parseDateOnly(value: string): Date | null {
  const normalized = toDateOnlyString(value);
  if (!normalized) return null;
  const d = parseISO(`${normalized}T12:00:00`);
  return Number.isNaN(d.getTime()) ? null : d;
}

export function formatRangeButtonLabel(
  start: string,
  end: string,
  placeholder = 'Select date range'
): string {
  if (!start && !end) return placeholder;
  if (start && !end) return `${formatDisplayShort(start)} – …`;
  if (!start && end) return `… – ${formatDisplayShort(end)}`;
  if (start === end) return formatDisplayShort(start);
  return `${formatDisplayShort(start)} – ${formatDisplayShort(end)}`;
}

export function formatDisplayShort(value: string): string {
  const d = parseDateOnly(value);
  if (!d) return value;
  return format(d, 'd MMM yyyy');
}

export function getMonthGridDays(anchor: Date): Date[] {
  const start = startOfWeek(startOfMonth(anchor), { weekStartsOn: 0 });
  const end = endOfWeek(endOfMonth(anchor), { weekStartsOn: 0 });
  return eachDayOfInterval({ start, end });
}

export function isDateInRange(
  day: Date,
  start: string,
  end: string,
  hoverEnd?: string | null
): boolean {
  const startD = parseDateOnly(start);
  if (!startD) return false;
  const endRaw = end || hoverEnd || '';
  const endD = parseDateOnly(endRaw);
  if (!endD) return isSameDay(day, startD);
  const [from, to] = startD <= endD ? [startD, endD] : [endD, startD];
  return isWithinInterval(day, { start: from, end: to });
}

export function isRangeEdge(
  day: Date,
  start: string,
  end: string,
  edge: 'start' | 'end'
): boolean {
  const startD = parseDateOnly(start);
  const endD = parseDateOnly(end || start);
  if (!startD || !endD) {
    if (edge === 'start' && startD) return isSameDay(day, startD);
    return false;
  }
  const [from, to] = startD <= endD ? [startD, endD] : [endD, startD];
  return edge === 'start' ? isSameDay(day, from) : isSameDay(day, to);
}

export function monthRangeStrings(year: number, monthIndex: number): {
  start: string;
  end: string;
} {
  const start = new Date(year, monthIndex, 1);
  const end = endOfMonth(start);
  return {
    start: toDateOnlyString(start),
    end: toDateOnlyString(end),
  };
}

/**
 * Calendar month for transaction list/reports filters.
 * `endExclusive` is the 1st of the next month — send as API `end_date` so backends
 * that treat the end bound as exclusive (or compare at midnight) still include the
 * last day of the month (e.g. July 31).
 */
export function transactionMonthDateRange(
  year: number,
  monthIndex: number
): { start: string; endInclusive: string; endExclusive: string } {
  const { start, end } = monthRangeStrings(year, monthIndex);
  const nextMonth = new Date(year, monthIndex + 1, 1);
  return {
    start,
    endInclusive: end,
    endExclusive: toDateOnlyString(nextMonth),
  };
}

export function yearRangeStrings(year: number): { start: string; end: string } {
  return {
    start: `${year}-01-01`,
    end: `${year}-12-31`,
  };
}

/** Year range with exclusive end (Jan 1 of following year) for transaction date filters. */
export function transactionYearDateRange(year: number): {
  start: string;
  endInclusive: string;
  endExclusive: string;
} {
  return {
    start: `${year}-01-01`,
    endInclusive: `${year}-12-31`,
    endExclusive: `${year + 1}-01-01`,
  };
}

export function buildYearOptions(
  centerYear: number,
  span = 12
): number[] {
  const half = Math.floor(span / 2);
  return Array.from({ length: span }, (_, i) => centerYear - half + i);
}

export function shiftAnchorMonth(anchor: Date, delta: number): Date {
  return delta < 0 ? subMonths(anchor, Math.abs(delta)) : addMonths(anchor, delta);
}

export function isOutsideMonth(day: Date, anchor: Date): boolean {
  return !isSameMonth(day, anchor);
}

/**
 * Month grid to show when opening the period picker.
 * - Specific month selected → that month
 * - "All months" + current year → today’s month (this month)
 * - "All months" + past year → December (latest month in that year)
 * - "All months" + future year → January
 */
export function resolvePeriodPickerAnchor(
  year: number,
  month: number,
  referenceDate: Date = new Date()
): Date {
  if (month >= 0) {
    return new Date(year, month, 1);
  }

  const refYear = referenceDate.getFullYear();
  const refMonth = referenceDate.getMonth();

  if (year < refYear) return new Date(year, 11, 1);
  if (year > refYear) return new Date(year, 0, 1);
  return new Date(year, refMonth, 1);
}
